/**
 * «Hvem er du?» i hvilemodus (#162, D6; Stell_Kjokken).
 *
 * Familiens medlemsknapper nederst på hvileskjermen. Et trykk åpner
 * PIN-skjermen for den personen (/login med medlemmet i state); Avbryt der
 * legger skjermen i hvile igjen. Trykk andre steder vekker appen som før.
 * Skjermen vekkes i klikket, idet vi navigerer bort: med `isIdle` sann på
 * /login kunne SwIdleReload laste siden på nytt midt i PIN-en.
 *
 * Medlemmene kommer fra GET /api/auth/roster, det samme som avatarrutenettet
 * i ProfileLogin, og bare på en innrullert kjøkkenskjerm (#142): først spør vi
 * /api/auth/device (uten innlogging), og er enheten ikke innrullert, vises
 * ingen rad og rosteret spørres aldri. Raden viser ikke mer enn rutenettet:
 * avatar og kortnavn. Underteksten fra skissen («3 dager på rad», «Voksen»)
 * er utelatt; den trenger streak-data som ikke finnes uten økt.
 *
 * Spørringene går bare når hvilemodus vises, så telefoner og vanlige
 * nettlesere (uten veggmodus) aldri gjør dem.
 */
import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AuthApi } from '../auth/authApi';
import type { LoginLocationState } from '../login/LoginRoute';
import { MemberButton } from '../login/MemberButton';
import { useDashboard, useIsIdle } from './DashboardStore';

/**
 * Markerer medlemsknappene. IdleMode vekker ikke på pointerdown her (knappen
 * vekker i klikket), ellers ble overlegget gjennomsiktig midt i trykket og
 * klikket landet i appen under (#162).
 */
export const WHO_ARE_YOU_MEMBER_ATTR = 'data-who-are-you-member';

const ROSTER_STALE_MS = 5 * 60_000;

export function WhoAreYouRow(): ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isIdle = useIsIdle();
  const wake = useDashboard((s) => s.wake);

  const device = useQuery({
    queryKey: ['auth', 'device'],
    queryFn: AuthApi.device,
    enabled: isIdle,
    staleTime: ROSTER_STALE_MS,
    retry: false,
  });
  const enrolled = device.data?.enrolled === true;
  const roster = useQuery({
    queryKey: ['auth', 'roster'],
    queryFn: AuthApi.roster,
    enabled: isIdle && enrolled,
    staleTime: ROSTER_STALE_MS,
    retry: false,
  });

  const members = enrolled ? (roster.data?.members ?? []) : [];
  if (members.length === 0) return null;

  return (
    <nav
      data-testid="idle-who-are-you"
      aria-label={t('dashboard.idle.who_are_you')}
      className="min-w-0 flex-1"
    >
      <p className="text-ink-2 mt-0 mb-3.5 text-lg font-bold">{t('dashboard.idle.who_are_you')}</p>
      <div className="flex flex-wrap gap-3 md:gap-4">
        {members.map((m) => (
          <span key={m.id} {...{ [WHO_ARE_YOU_MEMBER_ATTR]: '' }} className="contents">
            <MemberButton
              member={m}
              testId={`idle-member-${m.id}`}
              onClick={() => {
                const state: LoginLocationState = { memberId: m.id };
                wake();
                void navigate('/login', { state });
              }}
            />
          </span>
        ))}
      </div>
    </nav>
  );
}
